// The page bodies. One component per registry entry.
//
// Split from `app.tsx` so the composition root stays readable: `app.tsx` is config →
// gate → shell → routed page, and everything a page actually renders is here. Each
// component takes the same props, so the router needs no per-page wiring.

import * as React from 'react';
import { AnnaLauncher, buildAnnaUrl } from '@anshinus/anna-sdk-react';
import type { DemoConfig, DemoEnv } from '../demo-config';
import type { ResolvedSession } from '../auth/dto';
import {
  ALL_TIERS,
  ELEVATED_ROLE,
  READ_ONLY_ROLE,
  can,
  canWrite,
  describeTier,
  type Capabilities,
} from '../auth/capabilities';
import { probeSdk, targetMismatch, type WidenedUrlBuilder } from '../sdk-capabilities';
import { SDK_VERSION } from '../sdk-version';
import type { PrepSessionResult } from '../prep-session';
import {
  BRAND_PROFILE,
  BRAND_SURFACES,
  BRAND_SURFACE_SOURCE,
  BUILD_BRAND,
  agentName,
  brandIsCoherent,
  productName,
} from './brand';
import { PAGES } from './pages';

/**
 * The SDK's builder, widened for the capability probe. The cast is confined to this one
 * line, with the probe's structural type carrying the contract.
 */
const WIDENED_BUILD = buildAnnaUrl as unknown as WidenedUrlBuilder;

export const DEFAULT_SUMMARY = 'Reviewing the Q3 compliance checklist.';

/** Mutable demo state, owned by the shell so it survives page navigation. */
export interface DemoState {
  env: DemoEnv;
  summary: string;
  prep: PrepSessionResult | null;
  prepPending: boolean;
  lastLaunch: { url: string; trace: string } | null;
}

export interface PageProps {
  config: DemoConfig;
  session: ResolvedSession;
  caps: Capabilities;
  state: DemoState;
  /**
   * Guarded mutators. Every one re-checks the capability INSIDE itself — see the note in
   * `app.tsx`. A page cannot write by calling a setter directly because it is not given
   * one.
   */
  actions: {
    setEnv: (e: DemoEnv) => void;
    setSummary: (s: string) => void;
    runPrep: () => void;
    resetSandbox: () => void;
    onLaunch: (info: { url: string; trace: string }) => void;
  };
}

export function Fact({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <>
      <dt>{label}</dt>
      <dd className="mono" data-testid={testId}>
        {value}
      </dd>
    </>
  );
}

// ---------------------------------------------------------------------------

export function OverviewPage({ config, caps }: PageProps) {
  return (
    <>
      <p>
        {productName()} — a thin host application that embeds the{' '}
        <strong>published</strong> <code>{BRAND_PROFILE.reactPackage}</code> exactly
        as a real integrator does, behind the standard login.
      </p>
      <dl className="facts facts--stack">
        <Fact label="SDK package" value={BRAND_PROFILE.reactPackage} testId="sdk-package" />
        <Fact label="SDK version" value={SDK_VERSION} testId="sdk-version" />
        <Fact label="Image tag" value={config.imageTag} testId="image-tag" />
        <Fact label="Your access tier" value={caps.tier} testId="overview-tier" />
      </dl>
      <p className="note">
        The SDK is installed from the GitLab project 181 npm registry as a published
        tarball, not a workspace link — this is the artifact an integrator receives.
      </p>
      <p className="note" data-testid="brand-note">
        Chrome and design tokens come from the UI-SDK (<code>@anshinus/ui</code>), not from
        the agent SDK. This build ships as <code>{BUILD_BRAND}</code> with the{' '}
        <code>{BRAND_PROFILE.scope}</code> scope; the exact token
        package and import order are pending from Infra, so the seam is wired and the
        tokens are local placeholders.
      </p>
    </>
  );
}

export function EmbedPage({ config, session, caps, state, actions }: PageProps) {
  const contextRef = state.prep?.kind === 'ok' ? state.prep.contextRef : undefined;
  return (
    <>
      <p>
        The real <code>&lt;AnnaLauncher&gt;</code> from{' '}
        <code>{BRAND_PROFILE.reactPackage}@{SDK_VERSION}</code>, mounted as a consuming
        app&rsquo;s Header mounts it, carrying <strong>your</strong> tenant.
      </p>
      <div className="mountpoint" data-testid="launcher-mount">
        <AnnaLauncher
          src={config.callerSrc}
          tenant={session.tenantId}
          env={state.env}
          role="uat-tester"
          contextRef={contextRef}
          onOpen={actions.onLaunch}
        />
        <AnnaLauncher
          src={config.callerSrc}
          tenant={session.tenantId}
          env={state.env}
          role="uat-tester"
          contextRef={contextRef}
          iconOnly
          windowName="anna-icononly"
          onOpen={actions.onLaunch}
        />
      </div>
      <dl className="facts facts--stack">
        <Fact label="src" value={config.callerSrc} testId="param-src" />
        <Fact label="tenant" value={session.tenantId} testId="param-tenant" />
        <Fact label="env" value={state.env} testId="param-env" />
        <Fact label="context_ref" value={contextRef ?? '(none)'} testId="param-context-ref" />
      </dl>
      <p className="note">
        The URL the SDK will build for the next click, obtained by calling the SDK — not
        reconstructed here:
      </p>
      <p className="mono break" data-testid="preview-url">
        {previewUrl(config, session, state.env, contextRef)}
      </p>
      {state.lastLaunch && (
        <div className="panel panel--ok" data-testid="last-launch">
          <p className="mono break" data-testid="last-launch-url">
            {state.lastLaunch.url}
          </p>
          <p className="mono" data-testid="last-launch-trace">
            trace: {state.lastLaunch.trace}
          </p>
        </div>
      )}
      {!can(caps, 'launchAnna') && (
        <p className="mono" data-testid="embed-unreachable">
          unreachable: this page requires launchAnna
        </p>
      )}
    </>
  );
}

export function ContextPage({ config, caps, state, actions }: PageProps) {
  const mayWrite = canWrite(caps, 'writeSettings');
  return (
    <>
      <p>
        The SDK accepts a <code>contextRef</code> and puts it in the launch URL; it does
        not obtain one, so every integrator writes this call. It carries{' '}
        <strong>your</strong> Connect session cross-origin — never a service credential.
      </p>
      <label className="field">
        <span>rich_context.summary</span>
        <textarea
          data-testid="summary-input"
          value={state.summary}
          rows={2}
          readOnly={!mayWrite}
          aria-readonly={!mayWrite}
          onChange={(e) => actions.setSummary(e.target.value)}
        />
      </label>
      {!mayWrite && (
        <p className="note" data-testid="summary-read-only">
          The summary is read-only for the <code>{caps.tier}</code> tier.
        </p>
      )}
      <button
        type="button"
        className="action"
        data-testid="prep-session-button"
        disabled={state.prepPending}
        onClick={actions.runPrep}
      >
        {state.prepPending ? 'Preparing…' : 'Prepare rich context'}
      </button>
      {state.prep && (
        <div
          className={`panel ${state.prep.kind === 'ok' ? 'panel--ok' : 'panel--warn'}`}
          data-testid="prep-session-result"
          data-prep-kind={state.prep.kind}
        >
          <p className="mono">{describePrep(state.prep)}</p>
        </div>
      )}
      <p className="note">
        Target: <code data-testid="context-target">{config.baseUrl}</code>
      </p>
    </>
  );
}

/**
 * HOST INTEGRATION — the real wire-up, in two independent host applications.
 *
 * Host A is this shell. Host B is `host-b.html`, a SEPARATE DOCUMENT with its own bundle
 * entry, its own React root, its own `src` and its own `windowName`, loaded in an iframe.
 * Two React roots in one document would share a window and a popup-name namespace, which
 * is exactly where two real hosts collide — so the second host gets a real document.
 *
 * Both mount the SAME published artifact. Nothing here is mocked: clicking either button
 * opens the real Anna.
 */
export function HostIntegrationPage({ config, session, state }: PageProps) {
  const hostBUrl = `/host-b.html?tenant=${encodeURIComponent(session.tenantId)}&env=${state.env}`;
  return (
    <>
      <p>
        The whole point of the SDK is embedding into <strong>multiple</strong> host
        applications. Below, the same published{' '}
        <code>{BRAND_PROFILE.reactPackage}@{SDK_VERSION}</code> is mounted by two independent
        hosts — each with its own caller identity, its own popup name and, for host B, its
        own document.
      </p>

      <h3>Host A — this shell</h3>
      <p className="small">
        <code>src={config.callerSrc}</code>, default <code>windowName</code>.
      </p>
      <div className="mountpoint" data-testid="host-a-mount">
        <AnnaLauncher
          src={config.callerSrc}
          tenant={session.tenantId}
          env={state.env}
          role="uat-tester"
        />
      </div>

      <h3>Host B — a different application, in its own document</h3>
      <p className="small">
        <code>host-b.html</code> in an iframe: separate document, separate React root,
        separate bundle entry, <code>src=anshin-comply</code>,{' '}
        <code>windowName=anna-host-b</code>.
      </p>
      <iframe
        className="hostframe"
        title="Anshin Comply — a second host application"
        data-testid="host-b-frame"
        src={hostBUrl}
      />
      <p className="note">
        Host B receives the tenant from its parent, the way a real host application
        receives it from its own session. It refuses to mount a launcher without one
        rather than sending an empty <code>tenant</code> to Anna&rsquo;s audit chain.
      </p>

      <h3>The wire-up</h3>
      <p className="small">
        Install, then three lines. There is no provider, no context and no init call — the
        import order does not matter because the package has no module-level side effects.
      </p>
      <pre className="code" data-testid="host-wireup">{`npm install ${BRAND_PROFILE.reactPackage}@${SDK_VERSION}

import { AnnaLauncher } from '${BRAND_PROFILE.reactPackage}';

<AnnaLauncher
  src="${config.callerSrc}"      // YOUR app's brand-registry key
  tenant={tenantId}                 // from YOUR session, never a constant
  env="${state.env}"                       // dev | prod — the only target control
  windowName="my-app-anna"          // give each host its own, or two hosts share a popup
/>`}</pre>
      <p className="note">
        The SDK ships <strong>no stylesheet</strong>: the button renders
        <code> class=&quot;anna-launcher anna-launcher--with-label&quot;</code> and is
        otherwise unstyled. Every host writes those rules — this demo&rsquo;s are in{' '}
        <code>src/styles.css</code>.
      </p>

      <div className="panel panel--warn" data-testid="host-embed-gap">
        <h3>There is no inline embed, and this demo does not fake one</h3>
        <p>
          The published SDK is <strong>launcher-only</strong>. Its public surface is the
          launcher (<code>AnnaLauncher</code>, <code>AnshinShield</code>) plus URL and brand
          helpers (<code>buildAnnaUrl</code>, <code>generateTraceId</code>,{' '}
          <code>activeBrand</code>, <code>resolveBrandSurface</code>, …) — and, measured
          against the installed bundle, it contains no <code>iframe</code>, no{' '}
          <code>contentWindow</code> and no <code>embed</code> at all. The only route it
          ever builds is <code>/chat</code>.
        </p>
        <p>
          The <em>product</em> does have an inline surface (<code>/embed/chat</code>, with
          its own layout and hooks), so a host wanting Anna <em>inside</em> its own page
          today must hand-roll an iframe against a route the SDK does not expose or
          document. <strong>That is an SDK gap, raised rather than papered over here</strong> —
          hand-rolling it in this demo would show an integration the SDK does not actually
          provide.
        </p>
      </div>
    </>
  );
}

export function TargetPage({ config, state }: PageProps) {
  const sdkCaps = probeSdk(WIDENED_BUILD, state.env);
  const mismatch = targetMismatch(sdkCaps, config.baseUrl);
  return (
    <>
      <dl className="facts facts--stack">
        <Fact label="Configured base URL" value={config.baseUrl} testId="configured-base-url" />
        <Fact
          label="Origin the launcher will open"
          value={sdkCaps.launcherOrigin ?? `— (${sdkCaps.launcherError ?? 'unprobeable'})`}
          testId="launcher-origin"
        />
        <Fact
          label="SDK honours a baseUrl override"
          value={sdkCaps.baseUrlOverride ? 'yes' : 'no'}
          testId="baseurl-override-supported"
        />
        <Fact label="Selected environment" value={state.env} testId="target-env" />
      </dl>
      {sdkCaps.baseUrlOverride ? (
        <div className="panel" data-testid="baseurl-supported-note">
          <p>
            <strong>This SDK honours a <code>baseUrl</code> override.</strong> Probed at
            runtime by calling the published builder with a sentinel origin and checking it
            came back — <code>{BRAND_PROFILE.reactPackage}@{SDK_VERSION}</code> ships the
            brand-profile layer that <code>0.6.1</code> did not, so a host can point the
            launcher at any {agentName()} by passing <code>baseUrl</code>. This demo still
            mounts the launcher by <code>env</code> alone (targeting the deployed{' '}
            {state.env} {agentName()}), so the configured base URL above steers the
            demo&rsquo;s own <code>prep-session</code> call and is shown for comparison.
          </p>
        </div>
      ) : (
        <div className="panel panel--warn" data-testid="baseurl-unsupported-note">
          <p>
            <strong>This SDK version cannot be pointed at another {agentName()}.</strong> The
            installed <code>{BRAND_PROFILE.reactPackage}@{SDK_VERSION}</code> takes no{' '}
            <code>baseUrl</code> argument — probed at runtime by calling its URL builder
            with a sentinel origin and checking whether it came back. The host is resolved
            from a map baked into the bundle and selected by <code>env</code> alone. (If you
            see this on 0.7.x or later, the SDK regressed.)
          </p>
        </div>
      )}
      {mismatch && (
        <div className="panel panel--error" data-testid="target-mismatch">
          <p>
            <strong>The configured target is not the one the launcher will open.</strong>
          </p>
          <p className="mono">
            configured: {mismatch.configured}
            <br />
            launcher: {mismatch.launcher ?? '(could not be determined)'}
          </p>
        </div>
      )}
    </>
  );
}

export function IdentityPage({ session, caps }: PageProps) {
  return (
    <>
      <dl className="facts facts--stack" data-testid="identity-facts">
        <Fact label="sub (user_id)" value={session.sub} testId="identity-sub" />
        <Fact label="email" value={session.email} testId="identity-email" />
        <Fact label="display name" value={session.displayName ?? '(none)'} testId="identity-name" />
        <Fact label="tenant_id" value={session.tenantId} testId="identity-tenant" />
        <Fact
          label="is_platform_admin"
          value={session.isPlatformAdmin ? 'true' : 'false'}
          testId="identity-platform-admin"
        />
        <Fact
          label="roles"
          value={session.roles.length ? session.roles.join(', ') : '(none)'}
          testId="identity-roles"
        />
        <Fact label="session_id" value={session.sessionId} testId="identity-session" />
        <Fact label="expires_at" value={session.expiresAt} testId="identity-expires" />
        <Fact label="resolved tier" value={caps.tier} testId="identity-tier" />
      </dl>
      <p className="note">
        Assembled from <code>/api/auth/session/validate</code> and{' '}
        <code>/api/auth/me</code>. The tenant comes from <code>me.tenant.tenant_id</code>{' '}
        and from nowhere else — there is no configured tenant to fall back to.
      </p>
    </>
  );
}

export function SecurityPage({ caps }: PageProps) {
  const rows = [
    { key: 'viewPages', label: 'View pages' },
    { key: 'launchAnna', label: `Launch ${agentName()}` },
    { key: 'writeSettings', label: 'Write settings' },
    { key: 'resetSandbox', label: 'Reset sandbox' },
    { key: 'switchPersona', label: 'Switch persona back' },
  ] as const;
  return (
    <>
      <p>
        Fail-closed: the permissive tiers need an exact role match, and everything else —
        an unrecognised role, another SDK&rsquo;s role, zero roles — falls through to least
        privilege. There is no writable default.
      </p>
      <table className="caps" data-testid="capability-matrix">
        <thead>
          <tr>
            <th scope="col">Capability</th>
            {ALL_TIERS.map((t) => (
              <th key={t.tier} scope="col" data-testid={`caps-col-${t.tier}`}>
                {t.tier}
                {t.tier === caps.tier && <span className="caps__you"> (you)</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <th scope="row">{r.label}</th>
              {ALL_TIERS.map((t) => (
                <td key={t.tier} data-testid={`caps-${t.tier}-${r.key}`}>
                  {can(t, r.key) ? 'yes' : 'no'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="small">
        Elevated requires <code>{ELEVATED_ROLE}</code>; read-only requires{' '}
        <code>{READ_ONLY_ROLE}</code>. Your tier: <strong>{caps.tier}</strong> —{' '}
        {describeTier(caps)}.
      </p>

      <h3>Pages, and what each one needs</h3>
      <table className="caps" data-testid="page-matrix">
        <thead>
          <tr>
            <th scope="col">Page</th>
            <th scope="col">Requires</th>
            <th scope="col">You</th>
          </tr>
        </thead>
        <tbody>
          {PAGES.map((p) => (
            <tr key={p.id}>
              <th scope="row">{p.label}</th>
              <td className="mono">{p.requires ?? '—'}</td>
              <td data-testid={`page-access-${p.id}`}>
                {p.requires === null || can(caps, p.requires) ? 'open' : 'denied'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="note">
        Hiding a nav link is not access control: a page the session may not open is
        refused by the router too, not merely missing from the menu.
      </p>

      <h3>Brand surfaces</h3>
      <p className="note" data-testid="brand-coherent">
        {brandIsCoherent() ? 'coherent' : 'MISMATCHED'} — {BRAND_SURFACES.length} surfaces,
        all serving <code>{BRAND_SURFACE_SOURCE.stylesheet}</code>. A half-switched brand
        is worse than an unswitched one, so they are checked as a set.
      </p>
    </>
  );
}

export function SandboxPage({ caps, actions }: PageProps) {
  const mayReset = canWrite(caps, 'resetSandbox');
  return (
    <>
      <p>
        Resetting restores the target environment, the context summary and the launch
        history to their defaults. It is a <strong>write</strong>, so it is elevated-only.
      </p>
      <button
        type="button"
        className="action"
        data-testid="sandbox-reset"
        disabled={!mayReset}
        aria-disabled={!mayReset}
        onClick={actions.resetSandbox}
      >
        Reset demo sandbox
      </button>
      {!mayReset && (
        <p className="note" data-testid="sandbox-reset-denied">
          Denied for the <code>{caps.tier}</code> tier.
        </p>
      )}
    </>
  );
}

export function SettingsPage({ config, caps, state, actions }: PageProps) {
  const mayWrite = canWrite(caps, 'writeSettings');
  return (
    <>
      {!mayWrite && (
        <p className="note" data-testid="settings-read-only">
          Settings are read-only for the <code>{caps.tier}</code> tier — changing the
          target environment is a settings write.
        </p>
      )}
      <h3>Target environment</h3>
      <div className="envs" role="radiogroup" aria-label="Target environment">
        {(['dev', 'prod'] as const).map((e) => (
          <button
            key={e}
            type="button"
            role="radio"
            aria-checked={state.env === e}
            aria-disabled={!mayWrite}
            disabled={!mayWrite}
            data-testid={`env-option-${e}`}
            className={`envbtn${state.env === e ? ' envbtn--on' : ''}`}
            onClick={() => actions.setEnv(e)}
          >
            {e}
          </button>
        ))}
      </div>
      <dl className="facts facts--stack">
        <Fact label="caller src" value={config.callerSrc} testId="settings-caller-src" />
        <Fact label="product base URL" value={config.baseUrl} testId="settings-base-url" />
        <Fact
          label="Connect"
          value={config.connectBaseUrlOrNull ?? '(not configured — held)'}
          testId="settings-connect"
        />
        <Fact label="auth path" value={config.connect.authBasePath} testId="settings-auth-path" />
        <Fact label="app key" value={config.connect.appKey} testId="settings-app-key" />
      </dl>
      <p className="note">
        Caller src, base URLs and the Connect origin are deploy-time values from{' '}
        <code>/config.js</code>; they are shown here and changed in the values file, not in
        the browser.
      </p>
    </>
  );
}

/** Registry id → component. Exhaustive; the router falls back rather than indexing blind. */
export const PAGE_COMPONENTS: Record<string, React.ComponentType<PageProps>> = {
  overview: OverviewPage,
  hosts: HostIntegrationPage,
  embed: EmbedPage,
  context: ContextPage,
  target: TargetPage,
  identity: IdentityPage,
  security: SecurityPage,
  sandbox: SandboxPage,
  settings: SettingsPage,
};

function previewUrl(
  config: DemoConfig,
  session: ResolvedSession,
  env: DemoEnv,
  contextRef?: string,
): string {
  try {
    return buildAnnaUrl({
      src: config.callerSrc,
      tenant: session.tenantId,
      env,
      route: '/',
      role: 'uat-tester',
      trace: '<generated-per-click>',
      contextRef,
    });
  } catch (err) {
    return `builder threw: ${err instanceof Error ? err.message : String(err)}`;
  }
}

function describePrep(r: PrepSessionResult): string {
  switch (r.kind) {
    case 'ok':
      return `201 — context_ref ${r.contextRef}, expires ${r.expiresAt} (ttl ${r.ttlSeconds}s)`;
    case 'refused':
      return r.status === 0 ? `refused locally — ${r.detail}` : `HTTP ${r.status} — ${r.detail}`;
    case 'malformed':
      return `HTTP ${r.status} but the body does not satisfy the contract — ${r.detail}`;
    case 'network':
      return `no HTTP response — ${r.detail} (expected while Connect sends no Access-Control-Allow-Origin)`;
  }
}
